import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionBar, Header } from 'src/components/header';
import { SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { useDeleteLocationTag, useModal } from 'src/hooks';
import { ILocationTag } from 'src/interfaces';
import { useUserRole } from 'src/utils';
import { lang } from 'src/utils/language';

import { ActionsContainer } from '../locationListPage/LocationListPage.styled';
import { useLocationManageTagPage } from './hooks/ListLocationTagPage.hook';
import { ContentContainer, IconWrapper } from './ListLocationTagPage.styled';

export const ListLocationTagsPage = () => {
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();
  const deleteConfirmDialog = useModal();
  const [selectedLocationTag, setSelectedLocationTag] = useState(0);
  const [deletingStatus, setDeletingStatus] = useState<{
    [key: number]: boolean;
  }>({});

  const deleteLocationTag = useDeleteLocationTag();

  const {
    selectedSortOption,
    page,
    handlePageChange,
    totalPages,
    totalCount,
    isLoadingTags,
    columns,
    rows,
    sortItems,
  } = useLocationManageTagPage();

  const renderActions = (row: ILocationTag) => (
    <ActionsContainer>
      {!isViewOnly && (
        <>
          <IconWrapper
            aria-label="edit"
            onClick={() => navigate(PATHS.LOCATIONS_TAGS_EDIT(String(row.id)))}>
            <EditOutlinedIcon />
          </IconWrapper>
          <CustomTooltip
            width={400}
            placement="left"
            title={lang.deleteTag}
            isHidden={row.locations_count === 0}
            skipValidateOverflow={true}>
            <IconWrapper
              aria-label="delete"
              disabled={row.locations_count! > 0}
              onClick={() => {
                setSelectedLocationTag(row.id);
                deleteConfirmDialog.onOpenModal(() => deleteTag(row));
              }}>
              <DeleteOutlinedIcon />
            </IconWrapper>
          </CustomTooltip>
        </>
      )}
    </ActionsContainer>
  );

  const deleteTag = async (tag: ILocationTag) => {
    try {
      setDeletingStatus((prev) => ({ ...prev, [tag.id]: true }));
      await deleteLocationTag.mutateAsync(tag.id);
      Snackbar.success(`${tag?.name} ${lang.successfullyDeleted}`);
      deleteConfirmDialog.onCloseModal();
      handlePageChange(undefined as any, 1);
    } catch (error) {
      Snackbar.error(lang.failedToDeleteTag);
    } finally {
      setDeletingStatus((prev) => ({ ...prev, [tag.id]: false }));
    }
  };

  return (
    <Grid container width="100%" height="100%" direction="column" wrap="nowrap">
      <Header
        title="Manage tags"
        backLink={-1}
        actionBar={
          <ActionBar onHandleClick={() => navigate(PATHS.LOCATIONS_TAGS_ADD)} />
        }
        breadcrumbItems={[
          { label: 'Locations', link: PATHS.LOCATIONS },
          { label: 'Tags', link: PATHS.LOCATIONS_TAGS },
        ]}
      />
      {isLoadingTags ? (
        <Progress />
      ) : (
        <ContentContainer>
          <CustomTableWithPagination<{
            id: number;
            name: string;
            locations_count: number;
          }>
            columns={columns}
            rows={rows}
            loading={isLoadingTags}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            renderActions={(row) =>
              renderActions({
                ...row,
                created_at: '',
                modified_at: '',
              } as ILocationTag)
            }
            showTableControls
            selectedSortOption={selectedSortOption}
            sortItems={sortItems}
          />
        </ContentContainer>
      )}
      <SimpleDialog
        open={deleteConfirmDialog.open}
        title={lang.locationTagDeleteModal.title}
        confirmText={lang.deleteText}
        cancelText={lang.cancel}
        onClose={deleteConfirmDialog.onCloseModal}
        onClickCancel={deleteConfirmDialog.onCloseModal}
        disabled={deletingStatus[selectedLocationTag]}
        onClickConfirm={deleteConfirmDialog.customFn}
      />
    </Grid>
  );
};
